import { useEffect, useMemo, useState } from 'react'
import { useEuaEmissionPriceLast30Days } from '@/shared/hooks/euaEmissionPriceOverview'
import { computeCbamLiabilityEuro } from '@/shared/utils/computeCbamLiabilityEuro'
import { parseEuaPrice } from '@/shared/utils/parseEuaPrice'
import useI18n from '@/shared/hooks/usei18n'
import { Button, Card, CardBody, ModifierWidgetV2, Spinner } from '@/common'
import { EstimatedCbamLiabilityChart } from '@/common/components/EstimatedCbamLiabilityChart/EstimatedCbamLiabilityChart'
import { useCalculateBatchLiability } from '@/features/cbam-reporting/hooks/cbamLiability'
import { CscfControl } from './CscfControl'
import { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'

export default function EstimatedCbamLiabilityWidget({
  items
}: {
  items: {
    id?: string | null
    productionMethodCode: string
    netMassInTonnes: number
    isUsingDefaultEmissions: boolean
    emissionsReport?: SupplierEmissionsReport
    code: string
    country: string
  }[]
}) {
  const { t, currentLanguage, currencyFormatter2Decimals } = useI18n()

  const { data: euaEmissionPriceOverview } = useEuaEmissionPriceLast30Days()
  const weeklyAveragePrice = parseEuaPrice(euaEmissionPriceOverview?.weeklyAveragePrice)

  const [state, setState] = useState<{
    defaultPrice: number
    customPrice: number | null
    includeIndirectEmissions: boolean
    productionMethodCode: string
    cscfPercent: number
  }>({
    defaultPrice: 0,
    customPrice: null,
    includeIndirectEmissions: false,
    productionMethodCode: '',
    cscfPercent: 100
  })

  const [committedCscfPercent, setCommittedCscfPercent] = useState(100)

  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (state.productionMethodCode === '') {
        return true
      }
      return item.productionMethodCode === state.productionMethodCode
    })
  }, [items, state.productionMethodCode])

  const { data: liabilityData, isFetching: isChartLiabilityFetching } = useCalculateBatchLiability(
    {
      requests: filteredItems.map(item => {
        const id = item.id ?? `${item.code}-${item.country}-${item.netMassInTonnes}`
        return {
          id,
          code: item.code,
          country: item.country,
          volume: item.netMassInTonnes,
          isActualValues: !item.isUsingDefaultEmissions,
          directEmissionsPerTonne: item.isUsingDefaultEmissions
            ? null
            : item.emissionsReport?.specificDirectEmbeddedEmissions || null,
          indirectEmissionsPerTonne: item.isUsingDefaultEmissions
            ? null
            : item.emissionsReport?.specificIndirectEmbeddedEmissions || null,
          cscfPercent: committedCscfPercent
        }
      })
    },
    JSON.stringify({
      productionMethodCode: state.productionMethodCode,
      cscfPercent: committedCscfPercent
    })
  )

  const showChartLoading =
    filteredItems.length > 0 && (isChartLiabilityFetching || liabilityData === undefined)

  const onResetValues = () => {
    setCommittedCscfPercent(100)
    setState(prev => ({
      ...prev,
      customPrice: null,
      includeIndirectEmissions: false,
      cscfPercent: 100
    }))
  }

  const isPriceDifferent = state.customPrice !== null && state.customPrice !== weeklyAveragePrice
  const isCscfDifferent = state.cscfPercent !== 100
  const isResetValueEnabled =
    isPriceDifferent || state.includeIndirectEmissions !== false || isCscfDifferent

  useEffect(() => {
    if (state.customPrice === null && weeklyAveragePrice) {
      setState(prev => ({ ...prev, defaultPrice: weeklyAveragePrice }))
    }
  }, [weeklyAveragePrice, state.customPrice, setState])

  const handleCustomPriceChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, customPrice: Number(evt.target.value) }))
  }

  const handleCscfChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, cscfPercent: Number(evt.target.value) }))
  }

  const handleCscfCommit = (evt: React.SyntheticEvent<HTMLInputElement>) => {
    setCommittedCscfPercent(Number(evt.currentTarget.value))
  }

  const priceForCalculation = state.customPrice ?? weeklyAveragePrice ?? 0

  const chartStrings = useMemo(
    () => ({
      liabilityTooltip: t('common:estimatedCbamLiabilityChart.liabilityTooltip', {
        weeklyAveragePrice: currencyFormatter2Decimals(priceForCalculation)
      }),
      title: t('common:estimatedCbamLiabilityChart.chartTitle'),
      noGoods: t('common:estimatedCbamLiabilityChart.noGoods')
    }),
    [t, currencyFormatter2Decimals, priceForCalculation]
  )

  return (
    <Card>
      <CardBody>
        <div className="space-y-2 xl:grid xl:grid-cols-5 gap-4 ">
          <div className="col-span-3 space-y-2">
            <div className="relative">
              {showChartLoading && (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center bg-background-surface/70"
                  aria-busy="true"
                >
                  <Spinner className="size-8" />
                </div>
              )}
              <EstimatedCbamLiabilityChart
                data={Object.entries(liabilityData?.aggregatedProjection ?? {})
                  .filter(([year]) => {
                    const y = Number(year)
                    return y >= 2026 && y <= 2034
                  })
                  .map(([year, { netEmissions }]) => ({
                    year: Number(year),
                    liability: computeCbamLiabilityEuro(netEmissions, priceForCalculation)
                  }))}
                language={currentLanguage}
                strings={chartStrings}
              />
            </div>
          </div>
          <div className="col-span-2 space-y-2 relative">
            <ModifierWidgetV2
              title={t('common:estimatedLiability.weeklyAveragePrice.title')}
              value={priceForCalculation}
              sliderControls={{
                min: 0,
                max: 135,
                step: 1
              }}
              formatValue={value => currencyFormatter2Decimals(value)}
              tooltip={t('common:estimatedLiability.weeklyAveragePrice.tooltip', {
                price: currencyFormatter2Decimals(weeklyAveragePrice)
              })}
              onChange={handleCustomPriceChange}
            />
            <CscfControl
              value={state.cscfPercent}
              onChange={handleCscfChange}
              onMouseUp={handleCscfCommit}
            />
            <div className="xl:absolute bottom-0 right-0">
              <Button
                disabled={!isResetValueEnabled}
                sizeType="sm"
                variant="txt-default"
                onClick={onResetValues}
              >
                {t('common:estimatedLiability.actions.reset')}
              </Button>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
